import { useEffect, useState } from 'react'
import { Button, Checkbox, Grid, GridRow, Header } from 'semantic-ui-react'
import { Alert } from '@carsys/ui'
import Dropdown from '@cbgms/components/form/Dropdown'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getHaynesParams, postNewHaynesCustomer, putExistingHaynesCustomer } from 'app-support/licenses/state/actions'
import { IHaynesCustomer, IProductType, IReseller, IShopByCompany } from '@cbgms/api'
import { change, formValueSelector } from 'redux-form'
import { defaultHaynesLanguagesForCountry, haynesLanguageOptions } from './HaynesUtils'
import Input from '@cbgms/components/form/Input'
import { IRootReducerState } from 'state/reducers'

interface props {
  alreadyExists?: boolean
  form: string
  distributorID?: string
  clientID: string
  shop?: IShopByCompany
  companyCountry: string
  customer: IHaynesCustomer | null
  clientIDPostfix: string
  onCustomerCreate: () => void
}

const customerPropProductTypes = 'HaynesProCustomer.ProductTypes'
const customerPropLanguages = 'HaynesProCustomer.Languages'
const customerPropReseller = 'HaynesProCustomer.Reseller'
const customerPropPassword = 'HaynesProCustomer.Password'

export const HaynesProNewCustomer = ({
  form,
  distributorID,
  clientIDPostfix,
  shop,
  companyCountry,
  onCustomerCreate,
  alreadyExists,
  clientID,
  customer
}: props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [resellers, setResellers] = useState<IReseller[]>([])
  const [productTypes, setProductTypes] = useState<IProductType[]>([])
  const [message, setMessage] = useState('')
  const [useCustomPassword, setUseCustomPassword] = useState(alreadyExists)
  const selectedProductTypes = useSelector((state: IRootReducerState) => formValueSelector(form)(state, customerPropProductTypes))
  const selectedReseller = useSelector((state: IRootReducerState) => formValueSelector(form)(state, customerPropReseller))
  const selectedLanguages = useSelector((state: IRootReducerState) => formValueSelector(form)(state, customerPropLanguages))
  const password = useSelector((state: IRootReducerState) => formValueSelector(form)(state, customerPropPassword))

  // set a new default language whenever the country changes
  useEffect(() => {
    dispatch(change(form, customerPropLanguages, defaultHaynesLanguagesForCountry(companyCountry)))
  }, [companyCountry])

  useEffect(() => {
    if (!customer) return

    dispatch(
      change(
        form,
        customerPropProductTypes,
        customer.ProductTypes.map(productType => productType.ID)
      )
    )
    dispatch(change(form, customerPropReseller, customer.Reseller?.ID))
    dispatch(change(form, customerPropPassword, customer.Password))
  }, [customer?.CustomerID])

  useEffect(() => {
    const asyncFunc = async () => {
      try {
        if (distributorID) {
          setMessage('')
          const result = await dispatch(getHaynesParams(distributorID))
          setResellers(result.Data.Resellers.map(r => ({ ...r, text: r.Name, value: r.ID })))
          setProductTypes(result.Data.ProductType.map(p => ({ ...p, text: p.Name, value: p.ID })))
        } else {
          setMessage(t('haynespro.distributor_id_not_configured'))
        }
      } catch (e: any) {
        setMessage(e.toString())
        setResellers([])
        setProductTypes([])
      }
    }
    asyncFunc()
  }, [distributorID])

  const createCustomer = async () => {
    if (!selectedProductTypes?.length) {
      setMessage(t('haynespro.product_type_required_for_account'))
      return
    }
    try {
      setMessage('')
      const customerRequestData = {
        DistributorID: distributorID!,
        ClientID: clientID,
        ProductTypeIDs: selectedProductTypes?.map((pt: string) => Number(pt)) || [],
        ResellerID: selectedReseller,
        CompanyName: shop?.Name || '',
        Address1: `${shop?.VisitorAddress || ''} ${shop?.VisitorHouseNumber || ''}${shop?.VisitorHouseNumberAddition || ''}`,
        Zipcode: shop?.VisitorPostalCode,
        City: shop?.VisitorCity,
        CountryCode: companyCountry,
        Languages: selectedLanguages,
        ClientIDPostfix: clientIDPostfix,
        CustomerID: customer?.CustomerID,
        Password: useCustomPassword ? password : ''
      }
      if (!alreadyExists) {
        // totally new customer
        await dispatch(postNewHaynesCustomer(customerRequestData))
      } else {
        // override existing customer
        // finding the selected customer
        await dispatch(putExistingHaynesCustomer(customerRequestData))
      }
      onCustomerCreate()
    } catch (e: any) {
      setMessage(e.toString())
    }
  }

  return (
    <>
      <Header as='h3'>{t('Customer')}</Header>
      <Alert severity='info'>{t(!alreadyExists ? 'haynespro.customer_not_known_yet' : 'haynespro.customer_already_exists')}</Alert>
      <Grid>
        <GridRow>
          <Dropdown
            label={t('haynespro.select_reseller_dropdown')}
            name={customerPropReseller}
            data-testid='hayenes-dropdown-select-reseller'
            clearable
            search
            fluid
            className='full-width'
            selection
            options={resellers}
          />
        </GridRow>
        <GridRow>
          <Dropdown
            label={t('haynespro.select_product_type_dropdown')}
            data-testid='hayenes-dropdown-select-product_type'
            name={customerPropProductTypes}
            required
            fluid
            multiple
            search
            selection
            className='full-width'
            options={productTypes}
          />
        </GridRow>
        <GridRow>
          <Dropdown
            label={t('haynespro.select_language_dropdown')}
            data-testid='hayenes-dropdown-select-language'
            name={customerPropLanguages}
            required
            fluid
            multiple
            search
            selection
            className='full-width'
            options={haynesLanguageOptions}
          />
        </GridRow>
        {!alreadyExists && (
          <GridRow>
            <Checkbox
              checked={useCustomPassword}
              label={t('haynespro.use_custom_password')}
              onChange={() => setUseCustomPassword(!useCustomPassword)}
            />
          </GridRow>
        )}
        <GridRow>{useCustomPassword && <Input name={customerPropPassword} label={t('haynespro.customer_password')} />}</GridRow>
        <GridRow>
          <Button primary onClick={createCustomer}>
            {t(!alreadyExists ? 'haynespro.create_new_customer' : 'haynespro.edit_existing_customer')}
          </Button>
        </GridRow>
        {message && <Alert severity='error'>{message}</Alert>}
      </Grid>
    </>
  )
}

export default HaynesProNewCustomer
